import type { PlanningViewMode } from './ViewModeSelector';
import {
  getMondayOfWeek,
  addWeeks,
  addDays,
  addMonths,
  formatWeekRange,
  formatDayLabel,
  formatMonthLabel,
  getISOWeekNumber,
  getWeekType,
} from '../utils/dateUtils';

interface DateNavigatorProps {
  mode: PlanningViewMode;
  /** Reference date: the day for 'dag', the Monday for 'week', any day in month for 'maand' */
  date: Date;
  onDateChange: (date: Date) => void;
}

export function DateNavigator({ mode, date, onDateChange }: DateNavigatorProps) {
  const goPrev = () => {
    if (mode === 'dag') onDateChange(addDays(date, -1));
    else if (mode === 'week') onDateChange(addWeeks(date, -1));
    else onDateChange(addMonths(date, -1));
  };

  const goNext = () => {
    if (mode === 'dag') onDateChange(addDays(date, 1));
    else if (mode === 'week') onDateChange(addWeeks(date, 1));
    else onDateChange(addMonths(date, 1));
  };

  const goToday = () => {
    const today = new Date();
    if (mode === 'week') onDateChange(getMondayOfWeek(today));
    else onDateChange(today);
  };

  const prevLabel = mode === 'dag' ? 'Vorige dag' : mode === 'week' ? 'Vorige week' : 'Vorige maand';
  const nextLabel = mode === 'dag' ? 'Volgende dag' : mode === 'week' ? 'Volgende week' : 'Volgende maand';

  let rangeLabel: string;
  let subLabel: string | null = null;

  if (mode === 'dag') {
    rangeLabel = formatDayLabel(date);
  } else if (mode === 'week') {
    const monday = getMondayOfWeek(date);
    rangeLabel = formatWeekRange(monday);
    const weekNumber = getISOWeekNumber(monday);
    const weekType = getWeekType(monday);
    subLabel = `Week ${weekNumber} (${weekType})`;
  } else {
    rangeLabel = formatMonthLabel(date);
  }

  return (
    <div className="flex items-center gap-3">
      <div className="flex items-center border border-gray-200 rounded-lg overflow-hidden">
        <button
          type="button"
          onClick={goPrev}
          className="px-2.5 py-1.5 text-gray-600 hover:bg-gray-100 transition-colors"
          aria-label={prevLabel}
        >
          <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
        </button>
        <button
          type="button"
          onClick={goToday}
          className="px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-100 transition-colors border-x border-gray-200"
        >
          Vandaag
        </button>
        <button
          type="button"
          onClick={goNext}
          className="px-2.5 py-1.5 text-gray-600 hover:bg-gray-100 transition-colors"
          aria-label={nextLabel}
        >
          <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
        </button>
      </div>

      <div className="flex items-center gap-2">
        <span className="text-sm font-medium text-gray-900">{rangeLabel}</span>
        {subLabel && (
          <span className="text-xs px-2 py-0.5 rounded-full bg-gray-100 text-gray-600">
            {subLabel}
          </span>
        )}
      </div>
    </div>
  );
}
